// src/components/dashboard/TopNavBar.jsx
import React, { useState, useEffect, useRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  CalendarIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { getChatContacts } from '../../services/api';

const TopNavBar = memo(({
  toggleChatSidebar,
  isChatOpen,
  handleSignOut,
  currentUser,
  userRoleInInstitution,
  institutionName,
  globalUnreadCount,
  onUnreadCountUpdate
}) => {
  const { t } = useTranslation();
  
  // DEBUG: Log props received
  console.log('🔔 TopNavBar rendered with props:', {
    globalUnreadCount,
    isChatOpen,
    hasCurrentUser: !!currentUser,
    onUnreadCountUpdate: typeof onUnreadCountUpdate
  });

  const navigate = useNavigate();
  const location = useLocation();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const dropdownRef = useRef(null);

  const userFullName = currentUser?.display_name || currentUser?.displayName || t('nav.user');
  const displayRole = userRoleInInstitution;

  // Determine active section from current route
  const getActiveSection = () => {
    const path = location.pathname;
    if (path.includes('/calendar')) return 'calendar';
    if (path.includes('/courses')) return 'courses';
    if (path.includes('/students')) return 'students';
    if (path.includes('/profile')) return 'profile';
    return 'calendar';
  };

  const activeSection = getActiveSection();

  // Use globalUnreadCount if provided, otherwise use local state
  useEffect(() => {
    console.log('🔔 TopNavBar useEffect triggered:', {
      globalUnreadCount,
      isDefined: globalUnreadCount !== undefined,
      currentUnreadMessageCount: unreadMessageCount
    });
    
    if (globalUnreadCount !== undefined) {
      console.log('🔔 Setting unreadMessageCount to:', globalUnreadCount);
      setUnreadMessageCount(globalUnreadCount);
    }
  }, [globalUnreadCount]);

  // Check for unread messages
  const checkUnreadMessages = async () => {
    if (!currentUser) return;
    
    console.log('🔔 Manually checking unread messages via API...');
    try {
      const response = await getChatContacts();
      if (response.success && Array.isArray(response.data)) {
        const totalUnread = response.data.reduce((sum, contact) => {
          return sum + (contact.unread || 0);
        }, 0);
        console.log('🔔 API returned unread count:', totalUnread);
        setUnreadMessageCount(totalUnread);
        
        if (onUnreadCountUpdate) {
          onUnreadCountUpdate(totalUnread);
        }
      }
    } catch (error) {
      console.error('Failed to check unread messages:', error);
    }
  };

  useEffect(() => {
    // Only check for unread messages if globalUnreadCount is not provided
    if (globalUnreadCount === undefined) {
      console.log('🔔 globalUnreadCount is undefined, setting up API polling');
      checkUnreadMessages();
      const interval = setInterval(checkUnreadMessages, 30000);
      return () => clearInterval(interval);
    } else {
      console.log('🔔 globalUnreadCount is defined, skipping API polling');
    }
  }, [currentUser, globalUnreadCount]);

  useEffect(() => {
    if (window) {
      window.refreshUnreadCount = checkUnreadMessages;
    }
    return () => {
      if (window.refreshUnreadCount) {
        delete window.refreshUnreadCount;
      }
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);

  const handleChatToggle = () => {
    console.log('🔔 Chat toggle clicked, current unreadMessageCount:', unreadMessageCount);
    toggleChatSidebar();
    if (!isChatOpen) {
      setUnreadMessageCount(0);
    }
  };

  console.log('🔔 TopNavBar render - unreadMessageCount:', unreadMessageCount, 'should show badge:', unreadMessageCount > 0 && !isChatOpen);

  return (
    <div className="bg-white shadow-md border-b border-slate-200 sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0 flex items-center">
            <Link
              to="/dashboard"
              className="text-lg font-semibold text-cyan-700 hover:text-cyan-800 truncate max-w-[150px] md:max-w-[200px] transition-colors"
              title={institutionName || t('nav.portal')}
            >
              {institutionName || t('nav.portal')}
            </Link>
            {displayRole === 'teacher' && (
              <span className="ml-2 text-xs px-2 py-0.5 bg-purple-100 text-purple-700 font-medium rounded-full hidden lg:inline">
                {t('nav.teacherPortal')}
              </span>
            )}
            {displayRole === 'student' && (
              <span className="ml-2 text-xs px-2 py-0.5 bg-sky-100 text-sky-700 font-medium rounded-full hidden lg:inline">
                {t('nav.studentPortal')}
              </span>
            )}
          </div>

          <nav className="hidden md:flex md:ml-10 md:space-x-2 lg:space-x-4">
            <Link
              to="/calendar"
              className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeSection === 'calendar'
                  ? 'bg-cyan-50 text-cyan-600'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
              }`}
            >
              <CalendarIcon className="w-5 h-5 mr-1.5" />
              {t('nav.calendar')}
            </Link>
            <Link
              to="/courses"
              className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeSection === 'courses'
                  ? 'bg-cyan-50 text-cyan-600'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
              }`}
            >
              <BookOpenIcon className="w-5 h-5 mr-1.5" />
              {t('nav.myCourses')}
            </Link>
            {displayRole === 'teacher' && (
              <Link
                to="/students"
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSection === 'students'
                    ? 'bg-cyan-50 text-cyan-600'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                }`}
              >
                <UserGroupIcon className="w-5 h-5 mr-1.5" />
                {t('nav.students')}
              </Link>
            )}
          </nav>

          <div className="flex md:hidden items-center space-x-1">
            <Link
              to="/calendar"
              className={`p-2.5 rounded-lg ${
                activeSection === 'calendar'
                  ? 'bg-cyan-50 text-cyan-600'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <CalendarIcon className="w-5 h-5" />
            </Link>
            <Link
              to="/courses"
              className={`p-2.5 rounded-lg ${
                activeSection === 'courses'
                  ? 'bg-cyan-50 text-cyan-600'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <BookOpenIcon className="w-5 h-5" />
            </Link>
            {displayRole === 'teacher' && (
              <Link
                to="/students"
                className={`p-2.5 rounded-lg ${
                  activeSection === 'students'
                    ? 'bg-cyan-50 text-cyan-600'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <UserGroupIcon className="w-5 h-5" />
              </Link>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleChatToggle}
              className={`relative p-2 rounded-lg transition-colors ${
                isChatOpen ? 'bg-cyan-50 text-cyan-600' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
              }`}
              title={t('nav.chat')}
            >
              <ChatBubbleLeftRightIcon className="w-5 h-5" />
              {unreadMessageCount > 0 && !isChatOpen && (
                <span className="absolute top-1 right-1 flex items-center justify-center">
                  {unreadMessageCount <= 9 ? (
                    <span className="relative w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                      <span className="text-[10px] text-white font-bold">{unreadMessageCount}</span>
                    </span>
                  ) : (
                    <span className="relative">
                      <span className="absolute w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
                      <span className="relative w-2 h-2 bg-red-500 rounded-full block"></span>
                    </span>
                  )}
                </span>
              )}
            </button>

            {currentUser && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
                  aria-expanded={profileDropdownOpen}
                  aria-haspopup="true"
                >
                  <span className="text-sm font-medium text-slate-700 max-w-[120px] truncate" title={userFullName}>
                    {userFullName}
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 text-slate-500 transition-transform ${profileDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none origin-top-right py-1">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-800 truncate" title={userFullName}>{userFullName}</p>
                      <p className="text-xs text-slate-500 truncate" title={currentUser.email}>{currentUser.email}</p>
                      <p className="text-xs text-slate-500 capitalize mt-0.5">
                        {t('nav.role')}: <span className="font-medium text-slate-700">{t(`roles.${displayRole}`, displayRole)}</span>
                      </p>
                    </div>
                    <button 
                      onClick={() => { 
                        navigate('/profile'); 
                        setProfileDropdownOpen(false); 
                      }} 
                      className="flex items-center w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-cyan-600 transition-colors" 
                      role="menuitem"
                    >
                      <UserIcon className="w-4 h-4 mr-2.5 text-slate-400"/> 
                      {t('nav.profile')}
                    </button>
                    <button 
                      onClick={() => { 
                        handleSignOut(); 
                        setProfileDropdownOpen(false); 
                      }} 
                      className="flex items-center w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-red-600 transition-colors border-t border-slate-100" 
                      role="menuitem"
                    >
                      <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2.5 text-slate-400"/> 
                      {t('nav.signOut')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  // Only re-render if these specific props change
  return (
    prevProps.isChatOpen === nextProps.isChatOpen &&
    prevProps.currentUser?.id === nextProps.currentUser?.id &&
    prevProps.currentUser?.email === nextProps.currentUser?.email &&
    prevProps.currentUser?.display_name === nextProps.currentUser?.display_name &&
    prevProps.currentUser?.displayName === nextProps.currentUser?.displayName &&
    prevProps.userRoleInInstitution === nextProps.userRoleInInstitution &&
    prevProps.institutionName === nextProps.institutionName &&
    prevProps.globalUnreadCount === nextProps.globalUnreadCount
    // Don't compare functions (toggleChatSidebar, handleSignOut, onUnreadCountUpdate)
    // as they should be stable callbacks
  );
});

TopNavBar.displayName = 'TopNavBar';

export default TopNavBar;